import React, {useEffect, useState} from "react";
import {Button, ButtonGroup, DatePicker, Modal, Tabs, toaster} from "rsuite";
import Tab from "rsuite/Tab";
import moment from "moment";
import {ApiError} from "../../../api";
import {SharedApi} from "../../../services/Shared.api";
import {DeviceEvent} from "../../../models/Device.model";
import NotificationFactory from "../../../shared/Notifications";
import EventsTable from "../EventsTable/EventsTable";
import {
  DeviceEventsProps,
  DeviceEventsTab,
  EVENT_PAGE_SIZES
} from "./DeviceEvents.model";

const DeviceEvents = ({deviceId, disabled = false}: DeviceEventsProps) => {
  const [activeTab, setActiveTab] = useState<DeviceEventsTab>("events");
  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [storedEvents, setStoredEvents] = useState<DeviceEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [storedEventsLoading, setStoredEventsLoading] = useState(false);
  const [resetStoredEventsLoading, setResetStoredEventsLoading] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  useEffect(() => {
    setEvents([]);
    setStoredEvents([]);
    setNextCursor(null);
    setShowResetConfirmation(false);
  }, [deviceId]);

  const showLoadError = (error: unknown) => {
    if (error instanceof ApiError && error.status === 422) return;

    toaster.push(
      NotificationFactory.error("Ошибка при загрузке событий"),
      {placement: "topEnd"}
    );
  };

  const validateDateRange = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      toaster.push(
        NotificationFactory.warning("Дата начала не может быть позже даты окончания"),
        {placement: "topEnd"}
      );
      return false;
    }

    return true;
  };

  const formatDateParameter = (value: Date | null) => value
    ? moment(value).format("YYYY-MM-DD")
    : undefined;

  const getEvents = async (after?: string, append = false) => {
    // События из БД сервера читаются и когда станция не в сети
    if (!deviceId || !validateDateRange()) return;

    setEventsLoading(true);
    try {
      const response = await SharedApi.getDeviceEvents(
        String(deviceId),
        after,
        formatDateParameter(dateFrom),
        formatDateParameter(dateTo),
        pageSize
      );
      const receivedEvents = response?.events || [];

      setEvents((currentEvents) => append
        ? [...currentEvents, ...receivedEvents]
        : receivedEvents
      );
      setNextCursor(response?.next || null);
    } catch (error) {
      console.error("Ошибка при загрузке событий:", error);
      if (!append) {
        setEvents([]);
        setNextCursor(null);
      }
      showLoadError(error);
    } finally {
      setEventsLoading(false);
    }
  };

  const getStoredEvents = async () => {
    if (!deviceId || disabled) return;

    setStoredEventsLoading(true);
    try {
      const response = await SharedApi.getDeviceStoredEvents(String(deviceId));
      setStoredEvents(response?.events || []);
    } catch (error) {
      console.error("Ошибка при загрузке сохранённых событий:", error);
      setStoredEvents([]);
      showLoadError(error);
    } finally {
      setStoredEventsLoading(false);
    }
  };

  const resetStoredEvents = async () => {
    if (!deviceId || disabled) return;

    setResetStoredEventsLoading(true);
    try {
      await SharedApi.resetStoredEvents(String(deviceId));
      toaster.push(
        NotificationFactory.success("Список событий сброшен"),
        {placement: "topEnd"}
      );
      await getStoredEvents();
    } catch (error) {
      console.error("Ошибка при сбросе сохранённых событий:", error);
      if (!(error instanceof ApiError && error.status === 422)) {
        toaster.push(
          NotificationFactory.error("Ошибка при сбросе списка событий"),
          {placement: "topEnd"}
        );
      }
    } finally {
      setResetStoredEventsLoading(false);
      setShowResetConfirmation(false);
    }
  };

  const handleTabSelect = (eventKey?: string | number) => {
    setActiveTab(eventKey === "stored" ? "stored" : "events");
  };

  const resetCursor = () => {
    setNextCursor(null);
  };

  return (
    <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mt-2">
      <Tab eventKey="events" title="Список событий (БД)">
        <div className="row mt-3">
          <div className="col-md-12">
            <Button
              appearance="primary"
              onClick={() => getEvents()}
              loading={eventsLoading}
              disabled={eventsLoading || !deviceId}
            >
              Получить список событий
            </Button>
          </div>
        </div>

        <div className="row mt-2 align-items-end">
          <div className="col-xl-3 col-md-6 mt-2">
            <label className="d-block mb-1">Дата от</label>
            <DatePicker
              block
              value={dateFrom}
              onChange={(value) => {
                setDateFrom(value);
                resetCursor();
              }}
              format="dd.MM.yyyy"
              placeholder="Выберите дату начала"
              disabled={eventsLoading}
            />
          </div>

          <div className="col-xl-3 col-md-6 mt-2">
            <label className="d-block mb-1">Дата до</label>
            <DatePicker
              block
              value={dateTo}
              onChange={(value) => {
                setDateTo(value);
                resetCursor();
              }}
              format="dd.MM.yyyy"
              placeholder="Выберите дату окончания"
              disabled={eventsLoading}
            />
          </div>

          <div className="col-xl-6 col-md-12 mt-2">
            <label className="d-block mb-1">Количество событий</label>
            <ButtonGroup>
              {EVENT_PAGE_SIZES.map((size) => (
                <Button
                  key={size}
                  size="sm"
                  appearance={pageSize === size ? "primary" : "ghost"}
                  onClick={() => {
                    setPageSize(size);
                    resetCursor();
                  }}
                  disabled={eventsLoading}
                >
                  {size}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </div>

        <EventsTable
          events={events}
          loading={eventsLoading}
          title="Список событий"
        />

        {nextCursor && (
          <div className="mt-2">
            <Button
              appearance="ghost"
              onClick={() => getEvents(nextCursor, true)}
              loading={eventsLoading}
              disabled={eventsLoading}
            >
              Загрузить ещё
            </Button>
          </div>
        )}
      </Tab>

      <Tab eventKey="stored" title="Список событий">
        {disabled && (
          <div className="row mt-3">
            <div className="col-md-12 text-muted">
              Устройство не в сети — список событий из памяти устройства недоступен. События с сервера — на вкладке «Список событий (БД)».
            </div>
          </div>
        )}
        <div className="row mt-3">
          <div className="col-md-12 d-flex flex-wrap gap-2">
            <Button
              appearance="primary"
              onClick={getStoredEvents}
              loading={storedEventsLoading}
              disabled={disabled || storedEventsLoading || resetStoredEventsLoading || !deviceId}
            >
              Получить список событий
            </Button>
            <Button
              appearance="primary"
              color="red"
              onClick={() => setShowResetConfirmation(true)}
              disabled={disabled || storedEventsLoading || resetStoredEventsLoading || !deviceId}
            >
              Сбросить список событий
            </Button>
          </div>
        </div>

        <EventsTable
          events={storedEvents}
          loading={storedEventsLoading}
          title="Список событий (БД)"
        />

        <Modal
          open={showResetConfirmation}
          onClose={() => {
            if (!resetStoredEventsLoading) setShowResetConfirmation(false);
          }}
          backdrop="static"
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Подтверждение сброса</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Вы уверены, что хотите сбросить список событий? Это действие нельзя отменить.
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="subtle"
              onClick={() => setShowResetConfirmation(false)}
              disabled={resetStoredEventsLoading}
            >
              Отмена
            </Button>
            <Button
              appearance="primary"
              color="red"
              onClick={resetStoredEvents}
              loading={resetStoredEventsLoading}
              disabled={resetStoredEventsLoading}
            >
              Сбросить
            </Button>
          </Modal.Footer>
        </Modal>
      </Tab>
    </Tabs>
  );
};

export default DeviceEvents;
